import { inject, Injectable } from '@angular/core';
import type {
  OrderHistoryFilterSortPageModel,
  OrderHistoryModel,
  OrderHistoryRecordModel,
  ReceiptDocumentModel,
} from '@libs/services/models';
import { ConnectorService } from '@libs/shared/services';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OrderHistoryRepositoryService {
  private readonly connector: ConnectorService = inject(ConnectorService);

  public getOrderHistory(
    params: OrderHistoryFilterSortPageModel,
  ): Observable<OrderHistoryModel> {
    return this.connector.connect<OrderHistoryModel>(
      'service',
      'orderHistory',
      {
        searchParams: Object.fromEntries(
          this.createUrl(params),
        ),
      },
    );
  }

  public getOrderHistoryRecord(orderId: string): Observable<OrderHistoryRecordModel> {
    return this.connector.connect<OrderHistoryRecordModel>(
      'service',
      'orderHistoryRecord',
      {
        pathParams: {
          orderId,
        },
      },
    );
  }

  public getOrderDocuments(orderId: string): Observable<ReceiptDocumentModel[]> {
    return this.connector.connect<ReceiptDocumentModel[]>(
      'service',
      'orderDocuments',
      {
        pathParams: {
          orderId,
        },
      },
    );
  }

  public getOrderDocumentFile(fileName: string): Observable<Blob> {
    return this.connector.connect<Blob>(
      'service',
      'orderDocumentFile',
      {
        pathParams: {
          fileName,
        },
        responseType: 'blob',
      },
    );
  }

  private createUrl(params: OrderHistoryFilterSortPageModel): URLSearchParams {
    const queryParams: URLSearchParams = new URLSearchParams();

    queryParams.append('page', params.page.toString());
    queryParams.append('limit', params.limit.toString());
    queryParams.append('sort', params.sort.toString());

    const orderFromIso: string | null | undefined = params.filter.order?.from?.toISODate();

    if (orderFromIso) {
      queryParams.append('orderFrom', orderFromIso);
    }

    const orderToIso: string | null | undefined = params.filter.order?.to?.toISODate();

    if (orderToIso) {
      queryParams.append('orderTo', orderToIso);
    }

    const shippingFromIso: string | null | undefined = params.filter.shipping?.from?.toISODate();

    if (shippingFromIso) {
      queryParams.append('shippingFrom', shippingFromIso);
    }

    const shippingToIso: string | null | undefined = params.filter.shipping?.to?.toISODate();

    if (shippingToIso) {
      queryParams.append('shippingTo', shippingToIso);
    }

    if (params.filter.delivery !== undefined) {
      queryParams.append('delivery', params.filter.delivery);
    }

    return queryParams;
  }
}
